import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ShieldCheck, FileText, Trash2, BellRing } from "lucide-react";
import { toast } from "sonner";
import { parentApi, errMsg } from "../../lib/api";
import { isNative } from "../../lib/native";
import { useAuth } from "../../context/AuthContext";

export const AccountPanel = () => {
  const { parent, logout } = useAuth();
  const nav = useNavigate();
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);

  const del = async () => {
    setBusy(true);
    try {
      await parentApi.post("/auth/delete-account", { password: pw });
      toast.success("Your family account and data were deleted.");
      logout();
      nav("/");
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <div className="space-y-6">
      <h1 className="text-4xl font-bold">Account & privacy</h1>
      <div className="zn-card p-6 bg-white space-y-1">
        <p className="text-sm text-[#1B1A2E]/60">Signed in as</p>
        <p data-testid="account-email" className="font-display text-xl font-semibold">{parent?.name} · {parent?.email}</p>
      </div>
      <div className="zn-card p-6 bg-white">
        <h2 className="text-2xl font-bold mb-3 flex items-center gap-2"><BellRing size={22} /> Notifications</h2>
        <p className="text-[#1B1A2E]/75">{isNative ? "Push notifications are sent to this phone when something needs your review." : "Install the Zunii app to get push notifications. You'll still see alerts in the bell icon here."} Kids never receive push notifications.</p>
      </div>
      <div className="zn-card p-6 bg-white">
        <h2 className="text-2xl font-bold mb-3 flex items-center gap-2"><ShieldCheck size={22} /> Your family's privacy</h2>
        <ul className="list-disc pl-5 space-y-1 text-[#1B1A2E]/80 mb-4">
          <li>Kids use nicknames only, with no email, phone or location.</li>
          <li>No private messages between kids.</li>
          <li>No ads and no data selling.</li>
        </ul>
        <div className="flex flex-wrap gap-2">
          <Link to="/privacy" data-testid="account-privacy-link" className="zn-chip bg-white"><FileText size={15} /> Privacy Policy</Link>
          <Link to="/terms" data-testid="account-terms-link" className="zn-chip bg-white"><FileText size={15} /> Terms</Link>
          <Link to="/safety" data-testid="account-safety-link" className="zn-chip bg-white"><FileText size={15} /> Child Safety</Link>
        </div>
      </div>
      <div className="zn-card p-6 bg-[#FFE4EC]">
        <h2 className="text-2xl font-bold mb-2 flex items-center gap-2"><Trash2 size={22} /> Delete family account</h2>
        <p className="mb-4">This permanently deletes your account, every kid profile, comment and activity log, and removes all videos. It can't be undone.</p>
        {!confirm ? (
          <button data-testid="delete-account-start-btn" onClick={() => setConfirm(true)} className="zn-btn bg-white">Delete my account</button>
        ) : (
          <div className="flex flex-col sm:flex-row gap-3">
            <input data-testid="delete-account-password" type={parent?.has_password === false ? "email" : "password"} className="zn-input" placeholder={parent?.has_password === false ? "Type your account email to confirm" : "Enter your password to confirm"} value={pw} onChange={(e) => setPw(e.target.value)} />
            <button data-testid="delete-account-confirm-btn" disabled={!pw || busy} onClick={del} className="zn-btn bg-[#E8442A] text-white whitespace-nowrap">Delete forever</button>
          </div>
        )}
      </div>
    </div>
  );
};
